/**
 * Сортирует массив объектов по месяцам и годам и возвращает количество объектов в каждом месяце
 * @param {Array} data - массив объектов с полем createdAt
 * @returns {Object} - объект с количеством объектов по месяцам и годам
 */
export const sortByMonthAndYear = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {};
  }

  const result = {};

  data.forEach((item) => {
    if (!item.createdAt) return;
    const date = new Date(item.createdAt);
    const dateOut = new Date(item.updatedAt);
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Месяцы в JavaScript начинаются с 0
    
    const key = `${year}-${month < 10 ? '0' + month : month}`;
    
    if (!result[key]) {
      result[key] = {
        year,
        month,
        name: `${month < 10 ? '0' + month : month}.${year}`,
        IN: 0,
        OUT: 0,
        Ira: 0,
        Nati: 0,
        Shahar: 0,
        Ofek: 0,
        Ruti: 0,
        Storage: 0,
      };
    }
    
    result[key].IN += 1;

    // Счётчики по дизайнерам (на основе createdAt)
    const rawDesigner = item.designer ? String(item.designer).trim() : "";
    if (!rawDesigner) {
      result[key].Storage += 1;
    } else {
      const designer = rawDesigner.toLowerCase();
      if (designer === "ira") result[key].Ira += 1;
      else if (designer === "nati") result[key].Nati += 1;
      // else if (designer === "shahar") result[key].Shahar += 1;
      // else if (designer === "ofek") result[key].Ofek += 1;
      else if (designer === "ruti") result[key].Ruti += 1;
    }
    
    // OUT считаем только для записей из архива (по updatedAt — дата архивации)
    if (item._source === "archive" && item.updatedAt) {
      const yearOut = dateOut.getFullYear();
      const monthOut = dateOut.getMonth() + 1;
      
      const keyOut = `${yearOut}-${monthOut < 10 ? '0' + monthOut : monthOut}`;
      
      if (!result[keyOut]) {
        result[keyOut] = {
          year: yearOut,
          month: monthOut,
          name: `${new Date(yearOut, monthOut - 1).toLocaleString('en-US', { month: 'long' })} ${yearOut}`,
          IN: 0,
          OUT: 0,
          Ira: 0,
          Nati: 0,
          Shahar: 0,
          Ofek: 0,
          Ruti: 0,
          Storage: 0,
        };
      }
      
      result[keyOut].OUT += 1;
    }
  });

  // Сортировка по году и месяцу
  return Object.values(result).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
};


/**
 * Сортирует массив объектов по месяцам и годам и возвращает количество объектов по дизайнерам
 * @param {Array} data - массив объектов с полями createdAt и designer
 * @returns {Array} - массив с количеством объектов по месяцам и дизайнерам
 */
export const sortByDesignerAndMonthYear = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  const result = {};
  
  data.forEach((item) => {
    if (!item.printedAt || !item.designer) return;
    
    const date = new Date(item.printedAt);
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Месяцы в JavaScript начинаются с 0
    
    const key = `${year}-${month < 10 ? '0' + month : month}`;
    
    if (!result[key]) {
      result[key] = {
        year,
        month,
        name: `${month < 10 ? '0' + month : month}.${year}`,
        Ira: 0,
        Nati: 0,
        Shahar: 0,
        Ofek: 0,
        Ruti: 0,
      };
    }
    
    if (item.designer === "ira") {
      result[key].Ira += 1;
    }
    if (item.designer === "nati") {
      result[key].Nati += 1;
    }
    // if (item.designer === "shahar") {
    //   result[key].Shahar += 1;
    // }
    //if (item.designer === "ofek") {
    //  result[key].Ofek += 1;
    //}
    if (item.designer === "ruti") {
      result[key].Ruti += 1;
    }
  });

  // Сортировка по году и месяцу
  return Object.values(result).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
};

/**
 * Группирует заказы по месяцу + менеджеру.
 * @returns {{ rows: Array, managers: string[] }}
 *   rows — массив по месяцам с полями year, month, name, IN и количеством по каждому менеджеру.
 *   managers — список всех найденных менеджеров.
 */
export const sortByManagerAndMonthYear = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return { rows: [], managers: [] };
  }

  const result = {};
  const managersSet = new Set();
  // Менеджеры, которых не показываем на графике (учитываются только в IN)
  const EXCLUDED = new Set(["flori", "none", "office"]);

  data.forEach((item) => {
    if (!item.createdAt) return;
    const date = new Date(item.createdAt);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const key = `${year}-${month < 10 ? "0" + month : month}`;

    if (!result[key]) {
      result[key] = {
        year,
        month,
        name: `${month < 10 ? "0" + month : month}.${year}`,
        IN: 0,
        NoManager: 0,
      };
    }
    result[key].IN += 1;

    const rawManager = item.manager ? String(item.manager).trim() : "";
    if (!rawManager) {
      result[key].NoManager += 1;
      return;
    }
    const lower = rawManager.toLowerCase();
    if (EXCLUDED.has(lower) || lower.startsWith("studio")) return;

    // "Manager" — старое имя Ади, объединяем в "Adi"
    const normalized = lower === "manager" ? "Adi" : rawManager;

    managersSet.add(normalized);
    result[key][normalized] = (result[key][normalized] || 0) + 1;
  });

  const managers = Array.from(managersSet).sort();

  // Гарантируем, что в каждом месяце есть ключи для всех менеджеров (чтобы recharts корректно отрисовывал)
  const rows = Object.values(result)
    .map((row) => {
      managers.forEach((m) => {
        if (row[m] === undefined) row[m] = 0;
      });
      return row;
    })
    .sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

  return { rows, managers };
};

export const sortByMonthAndYearRips = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {};
  }

  const result = {};

  data.forEach((item) => {
    if (!item.printedAt) return;
    const date = new Date(item.printedAt);
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Месяцы в JavaScript начинаются с 0
    
    const key = `${year}-${month < 10 ? '0' + month : month}`;
    
    if (!result[key]) {
      result[key] = {
        year,
        month,
        name: `${month < 10 ? '0' + month : month}.${year}`,
        IN: 0,
        Ira: 0,
        Nati: 0,
        Shahar: 0,
        Ofek: 0,
        Ruti: 0,
        Storage: 0,
      };
    }
    
    result[key].IN += 1;

    // Счётчики по дизайнерам (на основе printedAt)
    const rawDesigner = item.designer ? String(item.designer).trim() : "";
    if (!rawDesigner) {
      result[key].Storage += 1;
    } else {
      const designer = rawDesigner.toLowerCase();
      if (designer === "ira") result[key].Ira += 1;
      else if (designer === "nati") result[key].Nati += 1;
      // else if (designer === "shahar") result[key].Shahar += 1;
      // else if (designer === "ofek") result[key].Ofek += 1;
      else if (designer === "ruti") result[key].Ruti += 1;
    }
    
  });

  // Сортировка по году и месяцу
  return Object.values(result).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
};



/**
 * Группирует данные по месяцам и возвращает общее количество метров для каждого месяца
 * @param {Array} data - массив объектов с полями date и metrs
 * @returns {Array} - массив с данными по месяцам
 */
export const sortFromFabricChart = (data) => {
  
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const result = {};

  data.forEach((item) => {
    if (!item.date || !item.metrs) return;
    
    const date = new Date(item.date);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    
    const key = `${year}-${month < 10 ? '0' + month : month}`;
    
    if (!result[key]) {
      result[key] = {
        year,
        month,
        name: `${month < 10 ? '0' + month : month}.${year}`,
        metrs: 0,
      };
    }
    
    result[key].metrs += Math.ceil(Number(item.metrs) * 1.02);
  });

  return Object.values(result).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
};

/**
 * Группирует данные по дням выбранного месяца
 * @param {Array} data - массив объектов с полями date и metrs
 * @param {number} year - год
 * @param {number} month - месяц (1-12)
 * @returns {Array} - массив с данными по дням
 */
export const sortFromFabricChartByDays = (data, year, month) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const result = {};

  data.forEach((item) => {
    if (!item.date || !item.metrs) return;
    
    const date = new Date(item.date);
    const itemYear = date.getFullYear();
    const itemMonth = date.getMonth() + 1;
    
    if (itemYear !== year || itemMonth !== month) return;
    
    const day = date.getDate();
    const key = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
    
    if (!result[key]) {
      result[key] = {
        year,
        month,
        day,
        name: `${day < 10 ? '0' + day : day}.${month < 10 ? '0' + month : month}.${year}`,
        metrs: 0,
      };
    }
    
    result[key].metrs += Math.ceil(Number(item.metrs) * 1.02);
  });

  return Object.values(result).sort((a, b) => a.day - b.day);
};